import { useCallback, useState } from 'react';
import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import { useSession } from '@/app/providers/session-provider';
import { useDownloadCertificate } from '@/shared/hooks/use-download-certificate';
import { resolveErrorMessage } from '@/shared/lib/errors';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { courseQueries } from '@/entities/course';
import {
  usersQueries,
  type ContentCompletionRequestResult,
  type GetContentCompletionResponse,
} from '@/entities/user';
import { AccountSummary } from '@/widgets/profile/account-summary';
import { CertificatesSection } from '@/widgets/profile/certificates-section';
import {
  CompletionRequestForm,
  type CourseContentOption,
} from '@/features/profile/completion-request-form';
import { CompletionRequests } from '@/widgets/profile/completion-requests';
import { PageContainer } from '@/shared/ui/page-container';

type TabKey = 'certificates' | 'requests';

export const ProfilePage = () => {
  const { user } = useSession();
  const [activeTab, setActiveTab] = useState<TabKey>('certificates');

  const {
    data: courseContentResponse,
    isPending: isCoursesLoading,
    isError: isCoursesError,
    error: coursesError,
  } = useQuery({
    ...courseQueries.list({ payment_status: 'paid' }),
  });

  const courseContentOptions: CourseContentOption[] = courseContentResponse?.length
    ? courseContentResponse.flatMap((course) =>
        course.contents.map((content) => ({
          id: content.id,
          title: content.title,
          courseTitle: course.title,
          courseId: course.id,
        })),
      )
    : [];

  const {
    data: certificates,
    isPending: isCertificatesLoading,
    isError: isCertificatesError,
    error: certificatesError,
  } = useQuery({
    ...usersQueries.certificates(),
    enabled: Boolean(user),
  });

  const completionRequestsQuery = useInfiniteQuery({
    ...usersQueries.completionRequests({
      page: 1,
      page_size: 10,
      pager: 'page_number',
    }),
    enabled: Boolean(user),
  });

  const completionRequestsData = completionRequestsQuery.data as
    | InfiniteData<GetContentCompletionResponse>
    | undefined;

  const completionRequests: ContentCompletionRequestResult[] =
    completionRequestsData?.pages.flatMap((page) => page.results) ?? [];

  const { download: downloadCertificate, isPending: isDownloading } =
    useDownloadCertificate();

  const showCoursesError = isCoursesError && coursesError;
  const coursesErrorMessage = showCoursesError
    ? resolveErrorMessage(
        showCoursesError,
        'We could not load your available courses.',
      )
    : undefined;

  const showCertificatesError = isCertificatesError && certificatesError;
  const certificatesErrorMessage = showCertificatesError
    ? resolveErrorMessage(showCertificatesError)
    : undefined;

  return (
    <main className="min-h-screen bg-muted/10 pb-20 pt-8">
      <PageContainer maxWidth="mobile">
        <div className="flex flex-col gap-4 md:gap-4">
          <AccountSummary name={user?.name} email={user?.email} />

          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as TabKey)}
            className="space-y-4"
          >
            <TabsList className="grid h-auto w-full grid-cols-2 gap-2 rounded-2xl bg-muted/50 p-1 sm:p-2 wrap-break-word">
              <TabsTrigger
                value="certificates"
                className="gap-2 rounded-xl px-4 py-3 text-xs font-semibold sm:px-6 sm:py-4 wrap-break-word whitespace-pre-line"
              >
                My Certificates
              </TabsTrigger>
              <TabsTrigger
                value="requests"
                className="gap-2 rounded-xl px-4 py-3 text-xs font-semibold sm:px-6 sm:py-4 wrap-break-word whitespace-pre-line"
              >
                Requests
              </TabsTrigger>
            </TabsList>

            <TabsContent value="certificates" className="mt-4 space-y-8">
              <CertificatesSection
                certificates={certificates ?? []}
                isLoading={isCertificatesLoading}
                errorMessage={certificatesErrorMessage}
                onDownload={downloadCertificate}
                isDownloading={isDownloading}
              />
            </TabsContent>

            <TabsContent value="requests" className="mt-4 space-y-10">
              <CompletionRequestForm
                courseContentOptions={courseContentOptions}
                isCoursesLoading={isCoursesLoading}
                coursesError={coursesErrorMessage}
              />
              <CompletionRequests
                completionRequests={completionRequests}
                isRequestsLoading={completionRequestsQuery.isPending}
                hasNextPage={Boolean(completionRequestsQuery.hasNextPage)}
                isFetchingNextPage={completionRequestsQuery.isFetchingNextPage}
                onLoadMore={completionRequestsQuery.fetchNextPage}
              />
            </TabsContent>
          </Tabs>
        </div>
      </PageContainer>
    </main>
  );
};
